import { useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Input } from "@shop-ledger/ui-components";
import { acceptInvite } from "../api/usersApi";
import { ApiError } from "../lib/apiClient";
import { StatusBanner } from "../components/StatusBanner";

/**
 * Public route (no auth) consuming a `setup_token` from `POST /users` (Staff invite) or
 * `POST /platform/tenants` — the other half of the invite-token flow `StaffPage` starts.
 * Since no email infrastructure exists, this is the page the link a new hire is handed
 * directly (Slack/WhatsApp/in person) would actually point at.
 */
export function AcceptInvitePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(params.get("token") ?? "");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await acceptInvite({ token, new_password: password });
      setDone(result.email);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not accept invite — the link may be invalid or expired.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <h1>Set your password</h1>
        <p>Complete your staff invite to ZEL-MART.</p>

        {done ? (
          <>
            <StatusBanner kind="info">
              Password set for {done}. You can now log in.
            </StatusBanner>
            <Button type="button" onClick={() => navigate("/login")}>
              Go to login
            </Button>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <Input label="Invite token" value={token} onChange={(e) => setToken(e.target.value)} required />
            <Input label="New password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} />
            <Input label="Confirm password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8} />
            {error ? <StatusBanner kind="error">{error}</StatusBanner> : null}
            <div className="form-actions">
              <Button type="submit" disabled={submitting}>
                {submitting ? "Setting password…" : "Set password"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
